import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type Release, type ReleaseKind } from '../../api';
import { useMe, useReleases } from '../../hooks';
import {
  formatDateTime,
  formatDay,
  formatImported,
  formatTimeShort,
  timeAgo,
} from '../../shared/format';
import { isManager } from '../../shared/persona';

const KIND_LABEL: Record<ReleaseKind, string> = {
  feat: '新增',
  fix: '修复',
  perf: '优化',
  refactor: '重构',
  docs: '文档',
  chore: '维护',
  other: '其他',
};

const STATUS_LABEL: Record<Release['files'][number]['status'], string> = {
  added: '新增',
  modified: '修改',
  deleted: '删除',
  renamed: '重命名',
  other: '变更',
};

const ENV_LABEL: Record<Release['environment'], string> = {
  production: '生产',
  staging: '预发布',
  baseline: '基线',
};

const CHANGE_LINE = /^(?:[-*]\s*)?(feat|fix|perf|refactor|docs|chore|other)\s*[:：]\s*(.+)$/i;

function parseChangeLines(raw: string): Array<{ kind: ReleaseKind; text: string }> {
  return raw
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const match = line.match(CHANGE_LINE);
      if (!match) return { kind: 'other' as ReleaseKind, text: line };
      return {
        kind: match[1].toLowerCase() as ReleaseKind,
        text: match[2].trim(),
      };
    });
}

function ReleaseCard({ release, latest }: { release: Release; latest: boolean }) {
  const files = release.files.slice(0, 80);
  return (
    <article className={`release-item${latest ? ' is-latest' : ''}`}>
      <div className="release-marker" aria-hidden>
        <i />
      </div>
      <div className="release-card">
        <header className="release-card-head">
          <div className="release-heading">
            <div className="release-meta-top">
              <span className="release-date">
                {formatDay(release.deployedAt)} <b>{formatTimeShort(release.deployedAt)}</b>
              </span>
              <span className="release-ago">{timeAgo(release.deployedAt)}</span>
              {latest ? <span className="badge accent">最新记录</span> : null}
              {release.environment === 'baseline' ? <span className="badge">基线</span> : null}
            </div>
            <h2>{release.title}</h2>
          </div>
          <div className="release-tags">
            <span className="badge accent mono">{release.version}</span>
            <span className="badge">{ENV_LABEL[release.environment]}</span>
          </div>
        </header>

        {release.summary ? <p className="release-summary">{release.summary}</p> : null}

        {release.changes.length ? (
          <ul className="change-list">
            {release.changes.map((change, index) => (
              <li key={`${release.id}-${index}`}>
                <span className={`change-kind kind-${change.kind}`}>
                  {KIND_LABEL[change.kind]}
                </span>
                <span>{change.text}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted release-no-change">本次没有附带变更说明。</p>
        )}

        <details className="release-files">
          <summary>
            <span>提交与文件</span>
            <span className="release-file-stats">
              {release.stats.commits > 0 ? `${release.stats.commits} 次提交 · ` : ''}
              {release.stats.files} 个文件
              {release.stats.insertions + release.stats.deletions > 0 ? (
                <>
                  {' '}
                  · <em className="diff-plus">+{release.stats.insertions}</em>{' '}
                  <em className="diff-minus">−{release.stats.deletions}</em>
                </>
              ) : null}
            </span>
          </summary>
          <div className="release-facts">
            <div>
              <span>分支</span>
              <b>{release.branch ?? '—'}</b>
            </div>
            <div>
              <span>提交</span>
              <b className="mono">{release.commitShort ?? '—'}</b>
            </div>
            <div>
              <span>发布人</span>
              <b>{release.deployedBy ?? '—'}</b>
            </div>
            <div>
              <span>完成时间</span>
              <b>{formatDateTime(release.deployedAt)}</b>
            </div>
          </div>
          {files.length ? (
            <ul className="release-file-list">
              {files.map((file) => (
                <li key={file.path}>
                  <span className={`file-status status-${file.status}`}>
                    {STATUS_LABEL[file.status]}
                  </span>
                  <code>{file.path}</code>
                </li>
              ))}
              {release.files.length > files.length ? (
                <li className="muted">另有 {release.files.length - files.length} 个文件未展开</li>
              ) : null}
            </ul>
          ) : (
            <p className="muted release-no-change">没有文件变更记录（基线或重新部署）。</p>
          )}
        </details>
      </div>
    </article>
  );
}

export default function Releases() {
  const me = useMe();
  const feed = useReleases();
  const queryClient = useQueryClient();
  const isAdmin = isManager(me.data?.persona ?? 'visitor');
  const list = feed.data?.releases ?? [];
  const latest = feed.data?.latest ?? list[0] ?? null;
  const latestId = latest?.id ?? null;
  const markedRef = useRef<number | null>(null);

  const [form, setForm] = useState({
    title: '',
    version: '',
    summary: '',
    changes: '',
    deployedAt: '',
  });
  const [notice, setNotice] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!latestId || markedRef.current === latestId) return;
    markedRef.current = latestId;
    api('/api/releases/seen', {
      method: 'POST',
      body: JSON.stringify({ releaseId: latestId }),
    })
      .then(() => queryClient.invalidateQueries({ queryKey: ['releases'] }))
      .catch(() => undefined);
  }, [latestId, queryClient]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setNotice(null);
    try {
      const result = await api<{ release: Release }>('/api/releases', {
        method: 'POST',
        body: JSON.stringify({
          title: form.title,
          version: form.version.trim() || undefined,
          summary: form.summary.trim() || undefined,
          changes: parseChangeLines(form.changes),
          deployedAt: form.deployedAt ? new Date(form.deployedAt).toISOString() : undefined,
        }),
      });
      setNotice({
        kind: 'ok',
        text: `已记录 ${result.release.version} · ${result.release.title}`,
      });
      setForm({ title: '', version: '', summary: '', changes: '', deployedAt: '' });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['releases'] }),
        queryClient.invalidateQueries({ queryKey: ['audit'] }),
      ]);
    } catch (error) {
      setNotice({
        kind: 'err',
        text:
          error instanceof ApiError && error.status === 403
            ? '只有管理员可以补录发布'
            : '记录失败，请检查标题与变更内容',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">Release Log</div>
          <h1>发布日志</h1>
          <p className="page-lead">
            记录每次上线的时间、提交范围、改动文件与功能变更；部署成功后自动写入，按上线时间倒序展示。
          </p>
        </div>
        <div className="release-head-stats">
          <div>
            <b>{list.length}</b>
            <span>条记录</span>
          </div>
          <div>
            <b>{latest?.version ?? '—'}</b>
            <span>当前版本</span>
          </div>
          <div>
            <b>{latest ? formatImported(latest.deployedAt) : '—'}</b>
            <span>最近记录</span>
          </div>
        </div>
      </div>

      {notice ? <div className={`notice ${notice.kind}`}>{notice.text}</div> : null}

      {isAdmin ? (
        <details className="release-compose">
          <summary>
            <span>补录一次发布</span>
            <span className="muted">管理员 · 用于流程外上线或历史补登</span>
          </summary>
          <form onSubmit={submit}>
            <div className="compose-grid">
              <div className="field">
                <label htmlFor="release-title">标题</label>
                <input
                  id="release-title"
                  className="input"
                  value={form.title}
                  onChange={(event) => setForm({ ...form, title: event.target.value })}
                  placeholder="例如：发布日志与上线提示"
                  maxLength={160}
                  required
                />
              </div>
              <div className="field">
                <label htmlFor="release-version">版本号（留空自动生成）</label>
                <input
                  id="release-version"
                  className="input"
                  value={form.version}
                  onChange={(event) => setForm({ ...form, version: event.target.value })}
                  placeholder="v2026.10.27.1"
                  maxLength={40}
                />
              </div>
            </div>
            <div className="field">
              <label htmlFor="release-summary">发布说明</label>
              <textarea
                id="release-summary"
                className="input textarea"
                value={form.summary}
                onChange={(event) => setForm({ ...form, summary: event.target.value })}
                placeholder="这次上线解决了什么、影响了谁。"
                rows={3}
                maxLength={6000}
              />
            </div>
            <div className="field">
              <label htmlFor="release-changes">变更内容（每行一条）</label>
              <textarea
                id="release-changes"
                className="input textarea mono"
                value={form.changes}
                onChange={(event) => setForm({ ...form, changes: event.target.value })}
                placeholder={'feat: 新增发布日志页面\nfix: 修复回放时间轴跳转\nchore: 更新部署脚本'}
                rows={5}
              />
            </div>
            <div className="compose-actions">
              <div className="field compose-time">
                <label htmlFor="release-time">上线时间（留空为现在）</label>
                <input
                  id="release-time"
                  className="input"
                  type="datetime-local"
                  value={form.deployedAt}
                  onChange={(event) => setForm({ ...form, deployedAt: event.target.value })}
                />
              </div>
              <button className="btn btn-primary" type="submit" disabled={busy}>
                {busy ? '写入中…' : '写入发布日志'}
              </button>
            </div>
          </form>
        </details>
      ) : null}

      {feed.isLoading ? (
        <div className="empty">加载发布记录…</div>
      ) : feed.isError ? (
        <div className="empty">
          <b>发布日志暂时不可用</b>
          请刷新页面或稍后再试。
        </div>
      ) : list.length ? (
        <section className="release-timeline" aria-label="发布记录">
          {list.map((release) => (
            <ReleaseCard key={release.id} release={release} latest={release.id === latestId} />
          ))}
        </section>
      ) : (
        <div className="empty">
          <b>还没有发布记录</b>
          下一次通过部署脚本上线后，这里会自动出现。
        </div>
      )}
    </>
  );
}